import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { AlertTriangle, ArrowLeft, BarChart3, Bell, CheckCircle2, CircleDollarSign, Clock } from "lucide-react"
import type { User } from "@pishgam/contracts"

import { api, monthName } from "../api"
import { LoadingCard, Page } from "../shared"
import { fa, type Step, type Task, type TasksResponse } from "../process"
import { ProcessTimeline } from "@/components/process-timeline"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"

const KIND_ROLE: Record<Task["kind"], string> = {
  allocation: "پرسنل", division: "مدیر واحد", unit: "مدیر کسب‌وکار", payroll: "مدیر مالی", costs: "مدیر مالی", close: "مدیر مالی", forecast: "مدیر کسب‌وکار",
}
const KIND_STEP: Partial<Record<Task["kind"], Step>> = { allocation: "allocation", division: "division", unit: "unit", payroll: "payroll", costs: "payroll", close: "close" }

export function HomePage({ user }: { user: User }) {
  const data = useQuery({ queryKey: ["process-tasks"], queryFn: () => api<TasksResponse>("/process/tasks") })
  const caps = user.capabilities
  const [ty, tm, td] = data.data?.cycle.today ?? [0, 0, 0]
  const open = data.data?.tasks.filter((task) => !task.done) ?? []

  return (
    <Page
      title={`${user.nameFa || "کاربر"}، خوش آمدید`}
      description={data.data ? `امروز ${fa(`${td} ${monthName(tm)} ${ty}`)}؛ در حال بستن حساب ${fa(data.data.cycle.periodLabel)}` : undefined}
    >
      {!data.data ? <LoadingCard /> : (
        <>
          {(data.data.tasks.length > 0 || !caps.companyReports) && <TaskList tasks={data.data.tasks} />}

          <Card>
            <CardHeader>
              <CardTitle>مراحل بستن حساب {fa(data.data.cycle.periodLabel)}</CardTitle>
              <CardDescription>{data.data.closed ? "این دوره بسته شده است." : `کل فرایند باید تا ${fa(data.data.cycle.deadlines.close.label)} بسته شود.`}</CardDescription>
            </CardHeader>
            <CardContent>
              <ProcessTimeline cycle={data.data.cycle} closed={data.data.closed} highlight={open.map((task) => KIND_STEP[task.kind]).filter((step): step is Step => Boolean(step))} />
              {caps.forecastUnits.length > 0 && !data.data.forecast.open && (
                <p className="mt-4 flex items-center gap-1.5 text-sm text-muted-foreground"><Clock className="size-4" aria-hidden /> پنجره بعدی بازنگری پیش‌بینی کسب‌وکار: {fa(`1 تا ${data.data.forecast.endLabel}`)}</p>
              )}
            </CardContent>
          </Card>

          <div className={cn("grid items-start gap-6", caps.companyReports && "lg:grid-cols-[minmax(0,1fr)_320px]")}>
            <Notifications />
            {caps.companyReports && (
              <section aria-labelledby="reports-title" className="grid gap-3">
                <h2 id="reports-title" className="text-sm font-semibold text-muted-foreground">گزارش‌ها</h2>
                <ReportLink to="/analytics/pnl" icon={BarChart3} title="سود و زیان" text="عملکرد ماهانه در برابر برنامه، برای هر شرکت." />
                <ReportLink to="/planning/fpa" icon={CircleDollarSign} title="برنامه‌ریزی مالی" text="برنامه سالانه و عملکرد هر کسب‌وکار." />
              </section>
            )}
          </div>
        </>
      )}
    </Page>
  )
}

/** Open work first (overdue at the top), one row each; the most urgent row carries the primary action. */
function TaskList({ tasks }: { tasks: Task[] }) {
  const open = tasks.filter((task) => !task.done).sort((a, b) => Number(b.overdue) - Number(a.overdue))
  const overdue = open.filter((task) => task.overdue).length
  if (!open.length) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-success/10 text-success"><CheckCircle2 className="size-5" aria-hidden /></span>
          <div>
            <p className="font-medium">کار بازی ندارید.</p>
            <p className="text-sm text-muted-foreground">سهم شما در این دوره انجام شده است. اگر موردی برگشت بخورد، همین‌جا نمایش داده می‌شود.</p>
          </div>
        </CardContent>
      </Card>
    )
  }
  return (
    <Card className="gap-0 py-0">
      <CardHeader className="flex flex-row flex-wrap items-baseline gap-x-3 gap-y-1 border-b py-4 [.border-b]:pb-4">
        <h2 id="tasks-title" className="text-lg font-semibold">{fa(open.length)} کار باز</h2>
        {overdue > 0 && <span className="inline-flex items-center gap-1 text-sm font-medium text-loss"><AlertTriangle className="size-4 self-center" aria-hidden /> {fa(overdue)} مورد از مهلت گذشته</span>}
      </CardHeader>
      <ul aria-labelledby="tasks-title" className="divide-y">
        {open.map((task, index) => <TaskRow key={task.key} task={task} primary={index === 0} />)}
      </ul>
    </Card>
  )
}

function TaskRow({ task, primary }: { task: Task; primary: boolean }) {
  return (
    <li className="flex items-center gap-3 px-4 py-4 sm:gap-4 md:px-6">
      <span className={cn("hidden size-9 shrink-0 place-items-center rounded-full sm:grid", task.overdue ? "bg-loss/10 text-loss" : "bg-muted text-muted-foreground")} aria-hidden>
        {task.overdue ? <AlertTriangle className="size-4" /> : <Clock className="size-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="flex items-center gap-1.5 font-medium">{task.overdue && <AlertTriangle className="size-4 shrink-0 text-loss sm:hidden" aria-hidden />}{fa(task.title)}</p>
          <span className="rounded-full border px-2 py-px text-xs text-muted-foreground">{KIND_ROLE[task.kind]}</span>
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {fa(task.detail)}
          {task.due && (
            <span className={cn("whitespace-nowrap", task.overdue && "font-medium text-loss")}>
              {" "}{task.overdue ? "مهلت گذشته: " : "مهلت: "}{fa(task.due)}
            </span>
          )}
        </p>
      </div>
      <Button asChild variant={primary ? "default" : "outline"} size="sm" className="shrink-0">
        <Link to={task.link}>{task.kind === "close" ? "پایش" : "انجام"} <ArrowLeft className="size-4" aria-hidden /></Link>
      </Button>
    </li>
  )
}

type Note = { id: number; titleFa: string; bodyFa: string; link: string; isRead: boolean; createdAt: string }

/** Notifications live on the home page, under the tasks: newest five, the rest on demand. */
function Notifications() {
  const client = useQueryClient()
  const [all, setAll] = useState(false)
  const notes = useQuery({ queryKey: ["notifications"], queryFn: () => api<Note[]>("/notifications") })
  const readAll = useMutation({ mutationFn: () => api<void>("/notifications/read-all", { method: "POST" }), onSuccess: () => client.invalidateQueries({ queryKey: ["notifications"] }) })
  const list = notes.data ?? []
  const unread = list.filter((note) => !note.isRead).length
  const shown = all ? list : list.slice(0, 5)
  return (
    <Card className="gap-0 py-0">
      <CardHeader className="flex min-h-14 flex-row items-center justify-between gap-2 border-b py-2.5 [.border-b]:pb-2.5">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Bell className="size-4 text-muted-foreground" aria-hidden /> اعلان‌ها
          {unread > 0 && <span className="rounded-full bg-primary px-2 py-px text-xs font-semibold text-primary-foreground tabular-nums">{fa(unread)} جدید</span>}
        </h2>
        {unread > 0 && <Button variant="ghost" size="sm" disabled={readAll.isPending} onClick={() => readAll.mutate()}>خواندن همه</Button>}
      </CardHeader>
      {notes.isLoading ? <p className="px-6 py-5 text-sm text-muted-foreground">در حال بارگذاری…</p> : !list.length ? (
        <p className="px-6 py-5 text-sm text-muted-foreground">اعلانی ندارید.</p>
      ) : (
        <ul className="divide-y">
          {shown.map((note) => (
            <li key={note.id}>
              <Link to={note.link || "/dashboard"} className="flex gap-3 px-5 py-3.5 transition-colors hover:bg-muted/50 focus-visible:bg-muted/60 focus-visible:outline-none md:px-6">
                <span aria-hidden className={cn("mt-2 size-2 shrink-0 rounded-full", note.isRead ? "bg-transparent" : "bg-primary")} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-start justify-between gap-3">
                    <span className={cn("text-sm", note.isRead ? "text-foreground/80" : "font-semibold")}>{fa(note.titleFa)}{!note.isRead && <span className="sr-only"> (خوانده نشده)</span>}</span>
                    <time dateTime={note.createdAt} className="shrink-0 text-xs text-muted-foreground">{new Date(note.createdAt).toLocaleDateString("fa-IR", { month: "long", day: "numeric" })}</time>
                  </span>
                  {note.bodyFa && <span className="mt-0.5 line-clamp-2 block text-sm text-muted-foreground">{fa(note.bodyFa)}</span>}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {list.length > 5 && (
        <div className="border-t px-3 py-2">
          <Button variant="ghost" size="sm" aria-expanded={all} onClick={() => setAll((value) => !value)}>{all ? "نمایش کمتر" : `نمایش همه (${fa(list.length)})`}</Button>
        </div>
      )}
    </Card>
  )
}

function ReportLink({ to, icon: Icon, title, text }: { to: string; icon: typeof BarChart3; title: string; text: string }) {
  return (
    <Link to={to} className="group flex items-start gap-3 rounded-xl border bg-card p-4 transition-colors hover:border-primary/50 hover:bg-primary/[0.03] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
      <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><Icon className="size-4" aria-hidden /></span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{title}</span>
        <span className="block text-sm text-muted-foreground">{text}</span>
      </span>
      <ArrowLeft className="mt-1 size-4 shrink-0 text-muted-foreground transition-transform group-hover:-translate-x-0.5 motion-reduce:transition-none" aria-hidden />
    </Link>
  )
}
